import { useState } from "react";
import { Star, Flag } from "lucide-react";
import { ReportDialog } from "@/components/ReportDialog";

export interface ReviewData {
  id: string;
  rating: number;
  title?: string | null;
  body?: string | null;
  buyerName?: string | null;
  sellerResponse?: string | null;
  createdAt: string;
}

function relativeDate(dateStr: string) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMon = Math.floor(diffDay / 30);
  if (diffMon < 12) return `${diffMon}mo ago`;
  return `${Math.floor(diffMon / 12)}y ago`;
}

export default function ReviewCard({ review }: { review: ReviewData }) {
  const [reportOpen, setReportOpen] = useState(false);
  return (
    <div className="space-y-2">
      {/* Stars + meta */}
      <div className="flex items-center gap-2">
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-4 w-4 ${
                star <= review.rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-muted-foreground"
              }`}
            />
          ))}
        </div>
        <span className="flex-1 text-xs text-muted-foreground">
          {review.buyerName || "Buyer"} &middot; {relativeDate(review.createdAt)}
        </span>
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          aria-label="Report review"
          className="text-muted-foreground/60 hover:text-muted-foreground"
        >
          <Flag className="h-3.5 w-3.5" />
        </button>
      </div>
      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType="review"
        targetId={review.id}
        targetLabel="review"
      />

      {/* Title */}
      {review.title && (
        <div className="text-sm font-semibold">{review.title}</div>
      )}

      {/* Body */}
      {review.body && (
        <p className="text-sm text-muted-foreground">{review.body}</p>
      )}

      {/* Seller response */}
      {review.sellerResponse && (
        <div className="ml-4 border-l-2 border-primary/30 pl-3 mt-2">
          <div className="text-xs font-semibold text-muted-foreground mb-0.5">
            Seller response
          </div>
          <p className="text-sm text-muted-foreground">{review.sellerResponse}</p>
        </div>
      )}
    </div>
  );
}
